// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/AlertsPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdvisor } from "../AdvisorContext";
import {
  ActionGroup,
  Button,
  Card,
  Section,
  StatusChip,
} from "../design-system/components";

const REQUEST_TIMEOUT_MS = 30000;

const SEVERITIES = ["all", "critical", "high", "medium", "low", "info"];
const STATUSES = ["all", "open", "acknowledged", "snoozed", "resolved"];

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}


function formatTimestamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatRelativeTime(value) {
  if (!value) return "—";

  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return "—";

  const diffMs = Date.now() - ts;
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function normalizeAlerts(payload) {
  if (Array.isArray(payload?.alerts)) return payload.alerts;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload)) return payload;
  return [];
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function cleanRouteValue(value) {
  const textValue = String(value ?? "").trim();
  const normalized = textValue.toLowerCase();

  if (
    !textValue ||
    normalized === "undefined" ||
    normalized === "null" ||
    normalized === "none" ||
    textValue === "—"
  ) {
    return "";
  }

  return textValue;
}

function includesNormalized(haystack, needle) {
  const h = normalizeText(haystack);
  const n = normalizeText(needle);
  if (!n) return false;
  return h.includes(n);
}

function alertMatchesRouteContext(alert, context) {
  if (!alert || !context) return false;

  const frameworkMatch = !context.framework
    ? true
    : normalizeText(alert.framework) === normalizeText(context.framework);

  const resourceMatch =
    !context.resourceId ||
    includesNormalized(alert.resource_id, context.resourceId) ||
    includesNormalized(alert.message, context.resourceId) ||
    includesNormalized(alert.title, context.resourceId);

  const controlMatch =
    !context.controlId ||
    includesNormalized(alert.control_id, context.controlId) ||
    includesNormalized(alert.rule_id, context.controlId) ||
    includesNormalized(alert.message, context.controlId) ||
    includesNormalized(alert.title, context.controlId);

  const ruleMatch =
    !context.ruleId ||
    includesNormalized(alert.rule_id, context.ruleId) ||
    includesNormalized(alert.control_id, context.ruleId) ||
    includesNormalized(alert.message, context.ruleId) ||
    includesNormalized(alert.title, context.ruleId);

  const hasAnyScopedField = Boolean(
    context.framework || context.controlId || context.ruleId || context.resourceId
  );

  return hasAnyScopedField && frameworkMatch && resourceMatch && controlMatch && ruleMatch;
}

function pickBestAlert(alerts, context) {
  if (!alerts.length) return null;
  if (!context) return alerts[0];

  const scored = alerts.map((alert) => {
    let score = 0;

    if (
      context.framework &&
      normalizeText(alert.framework) === normalizeText(context.framework)
    ) {
      score += 2;
    }

    if (context.resourceId) {
      if (normalizeText(alert.resource_id) === normalizeText(context.resourceId)) score += 8;
      else if (includesNormalized(alert.resource_id, context.resourceId)) score += 6;
      else if (includesNormalized(alert.message, context.resourceId)) score += 3;
      else if (includesNormalized(alert.title, context.resourceId)) score += 3;
    }

    if (context.controlId) {
      if (normalizeText(alert.control_id) === normalizeText(context.controlId)) score += 8;
      else if (normalizeText(alert.rule_id) === normalizeText(context.controlId)) score += 5;
      else if (includesNormalized(alert.message, context.controlId)) score += 3;
      else if (includesNormalized(alert.title, context.controlId)) score += 3;
    }

    if (context.ruleId) {
      if (normalizeText(alert.rule_id) === normalizeText(context.ruleId)) score += 8;
      else if (normalizeText(alert.control_id) === normalizeText(context.ruleId)) score += 5;
      else if (includesNormalized(alert.message, context.ruleId)) score += 3;
      else if (includesNormalized(alert.title, context.ruleId)) score += 3;
    }

    return { alert, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.score > 0 ? scored[0].alert : alerts[0];
}

function statusTone(value) {
  const status = normalizeText(value);

  if (status === "resolved") return "success";
  if (status === "acknowledged") return "informational";
  if (status === "snoozed") return "warning";
  return "danger";
}

function severityTone(value) {
  const severity = normalizeText(value);

  if (severity === "critical") return "danger";
  if (severity === "high") return "warning";
  if (severity === "low") return "success";
  return "informational";
}

function StatusPill({ value }) {
  const label = cleanRouteValue(value) || "open";

  return (
    <StatusChip tone={statusTone(label)}>
      {label.replace(/_/g, " ")}
    </StatusChip>
  );
}

function SeverityPill({ value }) {
  const label = cleanRouteValue(value) || "info";

  return (
    <StatusChip tone={severityTone(label)}>
      {label.replace(/_/g, " ")}
    </StatusChip>
  );
}

function MetricItem({ label, value, footnote, tone = "default" }) {
  return (
    <div className={`oasys-metric oasys-metric--compact alerts-metric alerts-metric--${tone}`}>
      <span className="oasys-metric__label">{label}</span>
      <strong className="oasys-metric__value">{value}</strong>
      {footnote ? (
        <span className="oasys-metric__supporting">{footnote}</span>
      ) : null}
    </div>
  );
}

function MetaField({ label, value, wrap = false }) {
  return (
    <div className="alerts-meta-field">
      <span>{label}</span>
      <strong className={wrap ? "is-wrap" : ""} title={String(value || "—")}>
        {value || "—"}
      </strong>
    </div>
  );
}

export default function AlertsPage({
  apiBaseUrl,
  getAccessToken,
  tenantId: tenantIdProp = "",
  userId: userIdProp = "",
  routeParams = {},
  setMode,
  themeMode = "dark",
}) {
  const { setCopilotContext, clearCopilotContext } = useAdvisor();
  const { getAccessTokenSilently } = useAuth0();

  const resolvedThemeMode =
    String(themeMode || "").trim().toLowerCase() === "light" ? "light" : "dark";
  const pageThemeClass =
    resolvedThemeMode === "light" ? "oasys-theme-light" : "oasys-theme-dark";

  const [severity, setSeverity] = useState(
    (cleanRouteValue(routeParams.severity) || "all").toLowerCase()
  );
  const [status, setStatus] = useState(
    (cleanRouteValue(routeParams.status) || "all").toLowerCase()
  );
  const [framework, setFramework] = useState(
    (cleanRouteValue(routeParams.framework) || "ALL").toUpperCase()
  );
  const [limit, setLimit] = useState(safeNumber(routeParams.limit, 25) || 25);

  // The tenant's persisted framework scope is authoritative.
  const [frameworkScope, setFrameworkScope] = useState(null);


  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [panelWarnings, setPanelWarnings] = useState([]);

  const [alertsSummary, setAlertsSummary] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [selectedAlertId, setSelectedAlertId] = useState(null);

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId = String(tenantIdProp || "").trim();
  const userId = String(userIdProp || "").trim();

  const routeSelectionContext = useMemo(() => {
    const routeFramework = cleanRouteValue(routeParams.framework);

    return {
      framework:
        routeFramework && routeFramework.toUpperCase() !== "ALL"
          ? routeFramework
          : "",
      controlId: cleanRouteValue(routeParams.controlId),
      ruleId: cleanRouteValue(routeParams.ruleId),
      resourceId: cleanRouteValue(routeParams.resourceId),
    };
  }, [routeParams]);

  useEffect(() => {
    const nextParams = {
      status,
      severity,
      framework,
      limit: String(limit),
      controlId: routeSelectionContext.controlId || undefined,
      ruleId: routeSelectionContext.ruleId || undefined,
      resourceId: routeSelectionContext.resourceId || undefined,
    };
    setMode("alerts", nextParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    severity,
    framework,
    limit,
    routeSelectionContext.controlId,
    routeSelectionContext.ruleId,
    routeSelectionContext.resourceId,
  ]);

  useEffect(() => {
    setSeverity((cleanRouteValue(routeParams.severity) || "all").toLowerCase());
    setStatus((cleanRouteValue(routeParams.status) || "all").toLowerCase());
    setFramework((cleanRouteValue(routeParams.framework) || "ALL").toUpperCase());
    setLimit(safeNumber(routeParams.limit, 25) || 25);
  }, [
    routeParams.severity,
    routeParams.status,
    routeParams.framework,
    routeParams.limit,
  ]);

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") return await getAccessToken();
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr(null);
    setPanelWarnings([]);

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort("alerts-page-timeout"),
      REQUEST_TIMEOUT_MS
    );

    (async () => {
      try {
        const token = await fetchToken();

        const requests = [
          {
            key: "summary",
            label: "Alerts summary",
            required: true,
            promise: getJsonWithToken(
              token,
              "/alerts/summary",
              {},
              controller.signal
            ),
          },
          {
            key: "recent",
            label: "Recent alerts",
            required: true,
            promise: getJsonWithToken(
              token,
              "/alerts/recent",
              { limit },
              controller.signal
            ),
          },
        ];

        const settled = await Promise.allSettled(requests.map((r) => r.promise));
        if (!mounted) return;

        let requiredFailure = null;
        const nextWarnings = [];
        let nextSummary = null;
        let nextAlerts = [];

        settled.forEach((result, index) => {
          const req = requests[index];

          if (result.status === "fulfilled") {
            if (req.key === "summary") nextSummary = result.value || null;
            if (req.key === "recent") nextAlerts = normalizeAlerts(result.value);
            return;
          }

          const reason = result.reason;
          const message =
            reason?.name === "AbortError"
              ? `${req.label} timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
              : String(reason?.message || reason || `${req.label} failed.`);

          if (req.required) {
            requiredFailure = `${req.label} failed: ${message}`;
          } else {
            nextWarnings.push(`${req.label} unavailable: ${message}`);
          }
        });

        if (requiredFailure) throw new Error(requiredFailure);

        setAlertsSummary(nextSummary);
        setRecentAlerts(nextAlerts);
        setPanelWarnings(nextWarnings);
      } catch (e) {
        if (!mounted) return;
        console.error("Alerts page load error:", e);
        setErr(String(e?.message || e));
      } finally {
        clearTimeout(timeoutId);
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchToken, getJsonWithToken, limit]);


  // Reuse Mission Control's existing organization framework authority.
  // Never substitute the global framework catalog when authority fails.
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setFrameworkScope(null);

    if (!tenantId || !userId) {
      setFrameworkScope({
        tenantId,
        frameworks: [],
        error: "Organization identity is unavailable.",
      });
      return () => { active = false; controller.abort(); };
    }

    (async () => {
      try {
        const token = await fetchToken();
        const result = await getJsonWithToken(
          token,
          "/dashboard/applied-frameworks",
          {},
          controller.signal
        );

        if (!active) return;

        const frameworks = Array.isArray(result?.frameworks)
          ? [...new Set(
              result.frameworks
                .map((value) => String(value || "").trim().toUpperCase())
                .filter(Boolean)
            )]
          : [];

        if (!frameworks.length) {
          throw new Error(
            "No applied frameworks are configured for this organization."
          );
        }

        setFrameworkScope({ tenantId, frameworks, error: "" });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setFrameworkScope({
          tenantId,
          frameworks: [],
          error: String(error?.message || error),
        });
      }
    })();

    return () => { active = false; controller.abort(); };
  }, [fetchToken, getJsonWithToken, tenantId, userId]);

  const appliedFrameworks =
    frameworkScope?.tenantId === tenantId && !frameworkScope.error
      ? frameworkScope.frameworks
      : [];

  const frameworkOptions = appliedFrameworks.length
    ? [
        { value: "ALL", label: "All" },
        ...appliedFrameworks.map((value) => ({
          value,
          label: value,
        })),
      ]
    : [];

  // An old deep link or changed organization scope must not retain
  // a framework that is no longer applied to the tenant.
  useEffect(() => {
    if (!appliedFrameworks.length) return;
    setFramework((current) =>
      current === "ALL" || appliedFrameworks.includes(current)
        ? current
        : "ALL"
    );
  }, [frameworkScope, tenantId]);

  const filteredAlerts = useMemo(() => {
    return recentAlerts.filter((alert) => {
      const matchesSeverity =
        severity === "all"
          ? true
          : String(alert.severity || "").toLowerCase() === severity;

      const normalizedStatus = String(alert.status || "open").toLowerCase();
      const matchesStatus = status === "all" ? true : normalizedStatus === status;

      const normalizedFramework = String(alert.framework || "").toUpperCase();
      const matchesFramework =
        framework === "ALL" ? true : normalizedFramework === framework;

      return matchesSeverity && matchesStatus && matchesFramework;
    });
  }, [recentAlerts, severity, status, framework]);

  useEffect(() => {
    if (!filteredAlerts.length) {
      setSelectedAlertId(null);
      return;
    }

    const explicitlySelectedStillExists = filteredAlerts.some(
      (alert) => alert.id === selectedAlertId
    );
    if (explicitlySelectedStillExists) return;

    const contextMatched = pickBestAlert(filteredAlerts, routeSelectionContext);
    setSelectedAlertId(contextMatched?.id || filteredAlerts[0].id);
  }, [filteredAlerts, selectedAlertId, routeSelectionContext]);

  const selectedAlert = useMemo(() => {
    return (
      filteredAlerts.find((alert) => alert.id === selectedAlertId) ||
      pickBestAlert(filteredAlerts, routeSelectionContext) ||
      filteredAlerts[0] ||
      null
    );
  }, [filteredAlerts, selectedAlertId, routeSelectionContext]);

  useEffect(() => {
    const alertId = String(
      selectedAlert?.alert_id || selectedAlert?.id || ""
    ).trim();
    const pageContext = {
      page: "alerts",
      surface: "alerts_page",
      intelligence_mode: alertId ? "contextual_explain" : "page",
      ...(framework && framework !== "ALL" ? { framework } : {}),
      ...(status ? { section: status } : {}),
      ...(severity ? { filter: severity } : {}),
    };

    if (!alertId) {
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    setCopilotContext({
      ...pageContext,
      record_type: "alert",
      alert_id: alertId,
      focus_label: String(
        selectedAlert?.title ||
          selectedAlert?.message ||
          selectedAlert?.rule_id ||
          "Selected alert"
      ),
      focus_type: "Alert",
    });
  }, [
    clearCopilotContext,
    framework,
    selectedAlert,
    setCopilotContext,
    severity,
    status,
  ]);

  const summaryMetrics = useMemo(() => {
    const byStatus = alertsSummary?.by_status || {};
    const bySeverity = alertsSummary?.by_severity || {};

    const openCount =
      safeNumber(byStatus.open) +
      safeNumber(byStatus.acknowledged) +
      safeNumber(byStatus.snoozed);

    const resolvedCount = safeNumber(byStatus.resolved);
    const criticalHighCount =
      safeNumber(bySeverity.critical) + safeNumber(bySeverity.high);

    return {
      openCount,
      resolvedCount,
      criticalHighCount,
      totalLoaded: filteredAlerts.length,
    };
  }, [alertsSummary, filteredAlerts]);

  const urgencyLine = useMemo(() => {
    if (summaryMetrics.openCount > 0) {
      return `${summaryMetrics.openCount} open alert${
        summaryMetrics.openCount === 1 ? "" : "s"
      } currently require attention.`;
    }
    return "No open alerts currently require action.";
  }, [summaryMetrics.openCount]);

  const contextLine = useMemo(() => {
    const parts = [];
    if (routeSelectionContext.framework) parts.push(`framework ${routeSelectionContext.framework}`);
    if (routeSelectionContext.controlId) parts.push(`control ${routeSelectionContext.controlId}`);
    if (routeSelectionContext.ruleId) parts.push(`rule ${routeSelectionContext.ruleId}`);
    if (routeSelectionContext.resourceId) parts.push(`resource ${routeSelectionContext.resourceId}`);

    if (!parts.length) return null;
    return `Scoped from Drift: ${parts.join(" · ")}`;
  }, [routeSelectionContext]);

  const openSelectedAlertInPosture = () => {
    if (!selectedAlert) return;

    setMode("posture", {
      framework:
        selectedAlert.framework || routeSelectionContext.framework || undefined,
      controlId:
        routeSelectionContext.controlId || selectedAlert.control_id || undefined,
      ruleId:
        routeSelectionContext.ruleId || selectedAlert.rule_id || undefined,
      resourceId:
        selectedAlert.resource_id || routeSelectionContext.resourceId || undefined,
    });
  };

  const pageHeader = (
    <header className="oasys-page-header alerts-page__header">
      <div className="oasys-page-header__content">
        <h1 className="oasys-page-header__title">Alerts</h1>
        <p className="oasys-page-header__description">
          Triage active alerts, inspect severity and status, and operate the
          compliance engine from a live action center.
        </p>
      </div>

      <ActionGroup align="end" density="compact" className="alerts-page__header-actions">
        <Button
          variant="secondary"
          size="medium"
          className="alerts-navigation-button"
          onClick={() => setMode("dashboard")}
        >
          Mission Control
        </Button>
        <Button
          variant="secondary"
          size="medium"
          className="alerts-navigation-button"
          onClick={() =>
            setMode("posture", {
              framework:
                selectedAlert?.framework ||
                routeSelectionContext.framework ||
                undefined,
              controlId: routeSelectionContext.controlId || undefined,
              ruleId: routeSelectionContext.ruleId || undefined,
              resourceId:
                selectedAlert?.resource_id ||
                routeSelectionContext.resourceId ||
                undefined,
            })
          }
        >
          Open Posture
        </Button>
      </ActionGroup>
    </header>
  );

  const pageStyles = (
    <style>{`
      .alerts-page {
        min-height: 100vh;
        color: var(--oasys-color-text-primary);
        background: var(--oasys-page-background) !important;
      }

      .alerts-page .alerts-page__shell {
        width: min(var(--oasys-content-max-width), 100%);
        margin-inline: auto;
        padding:
          var(--oasys-space-6)
          clamp(var(--oasys-space-4), 2.4vw, var(--oasys-space-8))
          var(--oasys-space-8);
        display: grid;
        gap: var(--oasys-space-5);
        color: var(--oasys-color-text-primary);
        background: transparent !important;
        border: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
      }

      .alerts-page__header {
        margin-bottom: 0;
      }

      .alerts-page__header-actions {
        flex-shrink: 0;
      }

      .alerts-navigation-button {
        min-width: 156px;
      }

      .oasys-theme-dark .alerts-navigation-button,
      :root[data-theme="dark"] .alerts-navigation-button {
        color: #172033;
        background: #f7f9fc;
        border-color: rgba(234, 240, 255, 0.22);
      }

      .oasys-theme-dark .alerts-navigation-button:hover:not(:disabled),
      :root[data-theme="dark"] .alerts-navigation-button:hover:not(:disabled) {
        background: #ffffff;
        border-color: rgba(234, 240, 255, 0.36);
      }

      .alerts-context {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--oasys-space-3);
        padding: var(--oasys-space-3) var(--oasys-space-4);
        color: var(--oasys-color-text-secondary);
        background: var(--oasys-color-surface-secondary);
        border: 1px solid var(--oasys-color-border);
        border-radius: var(--oasys-radius-control);
        font-size: var(--oasys-font-size-sm);
      }

      .alerts-warning {
        display: grid;
        gap: var(--oasys-space-2);
        padding: var(--oasys-space-4);
        color: var(--oasys-color-text-primary);
        background: var(--oasys-color-warning-soft);
        border: 1px solid color-mix(
          in srgb,
          var(--oasys-color-warning),
          transparent 68%
        );
        border-radius: var(--oasys-radius-control);
      }

      .alerts-warning strong {
        color: var(--oasys-color-warning);
      }

      .alerts-warning__items {
        display: flex;
        flex-wrap: wrap;
        gap: var(--oasys-space-2);
        color: var(--oasys-color-text-secondary);
        font-size: var(--oasys-font-size-xs);
      }

      .alerts-warning__items span {
        padding: 4px 8px;
        background: var(--oasys-color-surface-primary);
        border: 1px solid var(--oasys-color-border);
        border-radius: var(--oasys-radius-pill);
      }

      .alerts-overview .oasys-section__body {
        padding-top: 0;
      }

      .alerts-metric-rail {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        border-top: 1px solid var(--oasys-color-divider);
        margin:
          0
          calc(var(--oasys-space-4) * -1)
          calc(var(--oasys-space-4) * -1);
      }

      .alerts-metric {
        min-height: 118px;
        padding: var(--oasys-space-5) var(--oasys-space-4);
        justify-content: center;
      }

      .alerts-metric + .alerts-metric {
        border-left: 1px solid var(--oasys-color-divider);
      }

      .alerts-metric--danger .oasys-metric__value {
        color: var(--oasys-color-danger);
      }

      .alerts-metric--warning .oasys-metric__value {
        color: var(--oasys-color-warning);
      }

      .alerts-metric--success .oasys-metric__value {
        color: var(--oasys-color-success);
      }

      .alerts-metric--informational .oasys-metric__value {
        color: var(--oasys-color-info);
      }

      .alerts-queue-section .oasys-section__body {
        display: grid;
        gap: var(--oasys-space-4);
      }

      .alerts-filters {
        display: grid;
        gap: var(--oasys-space-3);
        padding: var(--oasys-space-4);
        background: var(--oasys-color-surface-secondary);
        border: 1px solid var(--oasys-color-border);
        border-radius: var(--oasys-radius-control);
      }

      .alerts-filter-row {
        display: grid;
        grid-template-columns: 92px minmax(0, 1fr);
        gap: var(--oasys-space-3);
        align-items: start;
      }

      .alerts-filter-label {
        padding-top: 7px;
        color: var(--oasys-color-text-muted);
        font-size: var(--oasys-font-size-xs);
        font-weight: var(--oasys-font-weight-semibold);
        letter-spacing: var(--oasys-letter-spacing-wide);
        text-transform: uppercase;
      }

      .alerts-filter-options {
        display: flex;
        flex-wrap: wrap;
        gap: var(--oasys-space-2);
      }

      .alerts-filter-chip {
        min-height: 30px;
        border-radius: var(--oasys-radius-pill);
        text-transform: capitalize;
      }

      .alerts-filter-chip.is-active {
        color: var(--oasys-color-text-inverse);
        background: var(--oasys-color-brand);
        border-color: var(--oasys-color-brand);
      }

      .alerts-filter-chip.is-active:hover:not(:disabled) {
        background: var(--oasys-color-brand-hover);
        border-color: var(--oasys-color-brand-hover);
      }

      .alerts-workspace {
        display: grid;
        grid-template-columns: minmax(0, 1.18fr) minmax(360px, 0.82fr);
        gap: var(--oasys-space-5);
        align-items: start;
        min-width: 0;
      }

      .alerts-queue-panel,
      .alerts-detail-panel {
        min-width: 0;
        background: var(--oasys-color-surface-secondary);
        border: 1px solid var(--oasys-color-border);
        border-radius: var(--oasys-radius-card);
        overflow: hidden;
      }

      .alerts-panel-heading {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: var(--oasys-space-3);
        padding: var(--oasys-space-4);
        border-bottom: 1px solid var(--oasys-color-divider);
      }

      .alerts-panel-heading h3 {
        margin: 0;
        font-size: var(--oasys-font-size-lg);
      }

      .alerts-panel-heading p {
        margin: var(--oasys-space-1) 0 0;
        color: var(--oasys-color-text-secondary);
        font-size: var(--oasys-font-size-sm);
      }

      .alerts-queue-scroll {
        height: 680px;
        overflow-y: auto;
        overflow-x: hidden;
        padding: var(--oasys-space-3);
        scrollbar-width: thin;
        scrollbar-color:
          var(--oasys-color-border-strong)
          var(--oasys-color-surface-secondary);
      }

      .alerts-queue-scroll::-webkit-scrollbar {
        width: 10px;
      }

      .alerts-queue-scroll::-webkit-scrollbar-track {
        background: var(--oasys-color-surface-secondary);
      }

      .alerts-queue-scroll::-webkit-scrollbar-thumb {
        background: var(--oasys-color-border-strong);
        border: 2px solid var(--oasys-color-surface-secondary);
        border-radius: var(--oasys-radius-pill);
      }

      .alerts-queue-list {
        display: grid;
        gap: var(--oasys-space-2);
      }

      .alerts-queue-row {
        width: 100%;
        appearance: none;
        text-align: left;
        font: inherit;
        color: var(--oasys-color-text-primary);
      }

      .alerts-queue-row.is-route-match:not(.oasys-card--selected) {
        border-left-color: var(--oasys-color-success);
      }

      .alerts-queue-row__chips {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--oasys-space-2);
        margin-bottom: var(--oasys-space-2);
      }

      .alerts-queue-row__title {
        margin: 0;
        color: var(--oasys-color-text-primary);
        font-size: var(--oasys-font-size-base);
        font-weight: var(--oasys-font-weight-semibold);
        line-height: var(--oasys-line-height-heading);
      }

      .alerts-queue-row__meta {
        display: flex;
        flex-wrap: wrap;
        gap: var(--oasys-space-2);
        margin-top: var(--oasys-space-2);
        color: var(--oasys-color-text-muted);
        font-size: var(--oasys-font-size-xs);
      }

      .alerts-queue-row__message {
        display: -webkit-box;
        margin: var(--oasys-space-2) 0 0;
        overflow: hidden;
        color: var(--oasys-color-text-secondary);
        font-size: var(--oasys-font-size-sm);
        line-height: var(--oasys-line-height-body);
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
      }

      .alerts-detail-panel {
        min-height: 680px;
      }

      .alerts-detail-body {
        display: grid;
        gap: var(--oasys-space-4);
        padding: var(--oasys-space-4);
      }

      .alerts-detail-chips {
        display: flex;
        flex-wrap: wrap;
        gap: var(--oasys-space-2);
      }

      .alerts-detail-title {
        margin: 0;
        color: var(--oasys-color-text-primary);
        font-size: var(--oasys-font-size-xl);
        line-height: var(--oasys-line-height-heading);
      }

      .alerts-detail-meta-line {
        display: flex;
        flex-wrap: wrap;
        gap: var(--oasys-space-2);
        color: var(--oasys-color-text-muted);
        font-size: var(--oasys-font-size-xs);
      }

      .alerts-detail-message {
        margin: 0;
        color: var(--oasys-color-text-secondary);
        font-size: var(--oasys-font-size-md);
        line-height: var(--oasys-line-height-body);
      }

      .alerts-meta-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--oasys-space-2);
      }

      .alerts-meta-field {
        min-width: 0;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: var(--oasys-space-3);
        min-height: 52px;
        padding: var(--oasys-space-3);
        background: var(--oasys-color-surface-primary);
        border: 1px solid var(--oasys-color-border);
        border-radius: var(--oasys-radius-control);
      }

      .alerts-meta-field span {
        flex: 0 0 auto;
        color: var(--oasys-color-text-muted);
        font-size: var(--oasys-font-size-xs);
      }

      .alerts-meta-field strong {
        min-width: 0;
        max-width: 66%;
        color: var(--oasys-color-text-primary);
        font-size: var(--oasys-font-size-sm);
        text-align: right;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .alerts-meta-field strong.is-wrap {
        overflow: visible;
        text-overflow: clip;
        white-space: normal;
        overflow-wrap: anywhere;
      }

      .alerts-detail-actions {
        display: grid;
        gap: var(--oasys-space-2);
        padding-top: var(--oasys-space-1);
      }

      .alerts-detail-actions__secondary {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--oasys-space-2);
      }

      .alerts-empty {
        padding: var(--oasys-space-6);
        color: var(--oasys-color-text-secondary);
        text-align: center;
      }

      .alerts-state-card {
        padding: var(--oasys-space-6);
      }

      .alerts-state-card strong {
        display: block;
        margin-bottom: var(--oasys-space-2);
      }

      .alerts-state-card p {
        margin: 0;
        color: var(--oasys-color-text-secondary);
      }

      @media (max-width: 1100px) {
        .alerts-workspace {
          grid-template-columns: 1fr;
        }

        .alerts-detail-panel {
          min-height: 0;
        }

        .alerts-queue-scroll {
          height: 600px;
        }
      }

      @media (max-width: 720px) {
        .alerts-page .alerts-page__shell {
          padding:
            var(--oasys-space-5)
            var(--oasys-space-4)
            var(--oasys-space-6);
        }
      }

      @media (max-width: 820px) {
        .alerts-page__header-actions {
          width: 100%;
          justify-content: flex-start;
        }

        .alerts-metric-rail {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .alerts-metric:nth-child(3) {
          border-left: 0;
          border-top: 1px solid var(--oasys-color-divider);
        }

        .alerts-metric:nth-child(4) {
          border-top: 1px solid var(--oasys-color-divider);
        }

        .alerts-filter-row,
        .alerts-meta-grid {
          grid-template-columns: 1fr;
        }

        .alerts-filter-label {
          padding-top: 0;
        }
      }

      @media (max-width: 560px) {
        .alerts-navigation-button {
          min-width: 0;
          width: 100%;
        }

        .alerts-metric-rail {
          grid-template-columns: 1fr;
        }

        .alerts-metric + .alerts-metric {
          border-left: 0;
          border-top: 1px solid var(--oasys-color-divider);
        }

        .alerts-detail-actions__secondary {
          grid-template-columns: 1fr;
        }
      }
    `}</style>
  );

  if (loading) {
    return (
      <div className={`alerts-page oasys-page-canvas ${pageThemeClass}`}>
        {pageStyles}
        <div className="oasys-page-shell alerts-page__shell">
          {pageHeader}
          <Card tone="primary" density="comfortable" className="alerts-state-card">
            <strong>Loading alerts…</strong>
            <p>Retrieving current alert posture and operational queue.</p>
          </Card>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className={`alerts-page oasys-page-canvas ${pageThemeClass}`}>
        {pageStyles}
        <div className="oasys-page-shell alerts-page__shell">
          {pageHeader}
          <Card tone="primary" density="comfortable" className="alerts-state-card">
            <strong style={{ color: "var(--oasys-color-danger)" }}>
              Alerts unavailable
            </strong>
            <p>Failed to load alerts: {err}</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`alerts-page oasys-page-canvas ${pageThemeClass}`}>
      {pageStyles}

      <div className="oasys-page-shell alerts-page__shell">
        {pageHeader}

        {contextLine ? (
          <div className="alerts-context">
            <span>{contextLine}</span>
            <StatusChip tone="informational">Scoped view</StatusChip>
          </div>
        ) : null}

        {panelWarnings.length > 0 ? (
          <div className="alerts-warning" role="status">
            <strong>Alerts loaded with partial data</strong>
            <div className="alerts-warning__items">
              {panelWarnings.map((warning, index) => (
                <span key={`${warning}-${index}`}>{warning}</span>
              ))}
            </div>
          </div>
        ) : null}

        <Section
          title="Alert Overview"
          description={urgencyLine}
          density="default"
          divided={false}
          className="alerts-overview"
        >
          <div className="alerts-metric-rail">
            <MetricItem
              label="Open"
              value={summaryMetrics.openCount}
              footnote="open + acknowledged + snoozed"
              tone={summaryMetrics.openCount > 0 ? "danger" : "success"}
            />
            <MetricItem
              label="Resolved"
              value={summaryMetrics.resolvedCount}
              footnote="closed operational load"
              tone="success"
            />
            <MetricItem
              label="Critical / High"
              value={summaryMetrics.criticalHighCount}
              footnote="highest urgency mix"
              tone={summaryMetrics.criticalHighCount > 0 ? "warning" : "success"}
            />
            <MetricItem
              label="Visible Alerts"
              value={summaryMetrics.totalLoaded}
              footnote="current filtered list"
              tone="informational"
            />
          </div>
        </Section>

        <Section
          title="Alert Queue"
          description="Filter the active operational queue, then inspect the selected alert without leaving the workbench."
          density="default"
          divided
          className="alerts-queue-section"
          actions={
            <StatusChip tone={filteredAlerts.length > 0 ? "informational" : "success"}>
              {filteredAlerts.length} visible
            </StatusChip>
          }
        >
          <div className="alerts-filters" aria-label="Alert filters">
            <div className="alerts-filter-row">
              <span className="alerts-filter-label">Severity</span>
              <div className="alerts-filter-options">
                {SEVERITIES.map((item) => (
                  <Button
                    key={item}
                    variant="secondary"
                    size="small"
                    className={`alerts-filter-chip ${
                      severity === item ? "is-active" : ""
                    }`}
                    aria-pressed={severity === item}
                    onClick={() => setSeverity(item)}
                  >
                    {item}
                  </Button>
                ))}
              </div>
            </div>

            <div className="alerts-filter-row">
              <span className="alerts-filter-label">Status</span>
              <div className="alerts-filter-options">
                {STATUSES.map((item) => (
                  <Button
                    key={item}
                    variant="secondary"
                    size="small"
                    className={`alerts-filter-chip ${
                      status === item ? "is-active" : ""
                    }`}
                    aria-pressed={status === item}
                    onClick={() => setStatus(item)}
                  >
                    {item}
                  </Button>
                ))}
              </div>
            </div>

            <div className="alerts-filter-row">
              <span className="alerts-filter-label">Framework</span>
              <div className="alerts-filter-options">
                {!frameworkOptions.length ? (
                  <span role={frameworkScope?.error ? "alert" : "status"}>
                    {frameworkScope?.tenantId === tenantId && frameworkScope.error
                      ? `Framework authority unavailable: ${frameworkScope.error}`
                      : "Loading organization frameworks…"}
                  </span>
                ) : null}
                {frameworkOptions.map((item) => (
                  <Button
                    key={item.value}
                    variant="secondary"
                    size="small"
                    className={`alerts-filter-chip ${
                      framework === item.value ? "is-active" : ""
                    }`}
                    aria-pressed={framework === item.value}
                    onClick={() => setFramework(item.value)}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="alerts-filter-row">
              <span className="alerts-filter-label">Range</span>
              <div className="alerts-filter-options">
                {[10, 25, 50].map((value) => (
                  <Button
                    key={value}
                    variant="secondary"
                    size="small"
                    className={`alerts-filter-chip ${
                      limit === value ? "is-active" : ""
                    }`}
                    aria-pressed={limit === value}
                    onClick={() => setLimit(value)}
                  >
                    Top {value}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="alerts-workspace">
            <div className="alerts-queue-panel">
              <div className="alerts-panel-heading">
                <div>
                  <h3>Live Alert Queue</h3>
                  <p>Newest matching alerts remain inside this bounded work queue.</p>
                </div>
                <StatusChip tone="informational">
                  {filteredAlerts.length} loaded
                </StatusChip>
              </div>

              <div className="alerts-queue-scroll">
                {filteredAlerts.length === 0 ? (
                  <div className="alerts-empty">
                    No alerts matched the current filters.
                  </div>
                ) : (
                  <div className="alerts-queue-list">
                    {filteredAlerts.map((alert) => {
                      const isSelected = selectedAlert?.id === alert.id;
                      const isRouteMatch = alertMatchesRouteContext(
                        alert,
                        routeSelectionContext
                      );

                      return (
                        <Card
                          key={alert.id}
                          as="button"
                          type="button"
                          tone="secondary"
                          density="compact"
                          interactive
                          selected={isSelected}
                          className={`alerts-queue-row ${
                            isRouteMatch ? "is-route-match" : ""
                          }`}
                          onClick={() => setSelectedAlertId(alert.id)}
                        >
                          <div className="alerts-queue-row__chips">
                            <SeverityPill value={alert.severity} />
                            <StatusPill value={alert.status} />
                            {isRouteMatch ? (
                              <StatusChip tone="success">Drift match</StatusChip>
                            ) : null}
                          </div>

                          <h4 className="alerts-queue-row__title">
                            {alert.title || "Untitled alert"}
                          </h4>

                          <div className="alerts-queue-row__meta">
                            <span>{alert.alert_type || "alert"}</span>
                            {alert.framework ? <span>{alert.framework}</span> : null}
                            {alert.resource_id ? (
                              <span>{alert.resource_id}</span>
                            ) : null}
                            <span>{formatRelativeTime(alert.created_at)}</span>
                          </div>

                          <p className="alerts-queue-row__message">
                            {alert.message || "No alert message available."}
                          </p>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <aside className="alerts-detail-panel">
              <div className="alerts-panel-heading">
                <div>
                  <h3>Selected Alert</h3>
                  <p>Operational context and available next actions.</p>
                </div>
              </div>

              {!selectedAlert ? (
                <div className="alerts-empty">
                  Select an alert to inspect details.
                </div>
              ) : (
                <div className="alerts-detail-body">
                  <div className="alerts-detail-chips">
                    <SeverityPill value={selectedAlert.severity} />
                    <StatusPill value={selectedAlert.status} />
                  </div>

                  <h3 className="alerts-detail-title">
                    {selectedAlert.title || "Untitled alert"}
                  </h3>

                  <div className="alerts-detail-meta-line">
                    <span>{selectedAlert.alert_type || "alert"}</span>
                    {selectedAlert.framework ? (
                      <span>{selectedAlert.framework}</span>
                    ) : null}
                    {selectedAlert.resource_id ? (
                      <span>{selectedAlert.resource_id}</span>
                    ) : null}
                  </div>

                  <p className="alerts-detail-message">
                    {selectedAlert.message || "No alert message available."}
                  </p>

                  <div className="alerts-meta-grid">
                    <MetaField
                      label="Created"
                      value={formatTimestamp(selectedAlert.created_at)}
                    />
                    <MetaField
                      label="Updated"
                      value={formatTimestamp(selectedAlert.updated_at)}
                    />
                    <MetaField
                      label="Framework"
                      value={selectedAlert.framework || "—"}
                    />
                    <MetaField
                      label="Resource"
                      value={selectedAlert.resource_id || "—"}
                      wrap
                    />
                  </div>

                  <div className="alerts-detail-actions">
                    <Button variant="primary" size="medium" fullWidth>
                      Acknowledge
                    </Button>

                    <div className="alerts-detail-actions__secondary">
                      <Button variant="secondary" size="medium" fullWidth>
                        Snooze
                      </Button>
                      <Button variant="secondary" size="medium" fullWidth>
                        Resolve
                      </Button>
                    </div>

                    <Button
                      variant="secondary"
                      size="medium"
                      fullWidth
                      onClick={openSelectedAlertInPosture}
                    >
                      Open in Posture
                    </Button>
                  </div>
                </div>
              )}
            </aside>
          </div>
        </Section>
      </div>
    </div>
  );
}
